/**
 * React-compatible Pinia Options API facade backed by per-property Jotai atoms.
 *
 * Port of createJotaiPiniaFacadeForVue — identical store semantics
 * (state atom map, getters, actions, `this`-bound proxy) but uses
 * useAtomValue for React re-renders instead of Vue's reactive().
 *
 * The returned factory function is a React hook. Call it once at the
 * top level of each component:
 *
 *   const store = useDtfStore();
 *   // then use store.someState, store.someGetter, store.someAction(...)
 *
 * Do NOT call it inside nested functions — it follows React rules of hooks.
 */

import { atom } from "jotai/vanilla";
import { useAtomValue } from "jotai/react";
import { setAutoFreeze } from "immer";

type WritableAtom<T = unknown> = { init: T };
type AtomMap = Record<string, WritableAtom>;

/**
 * Minimal interface for a Jotai store. Broad enough to accept
 * the return value of `createStore()` from `jotai/vanilla`.
 */
type JotaiStore = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get(a: any): any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set(a: any, value: any): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sub(a: any, callback: () => void): () => void;
};

/* ── Generic helpers ─────────────────────────────────────────────── */

/** Infer the value type out of an atom `{ init: T }`. */
type InferAtomType<A> = A extends WritableAtom<infer T> ? T : unknown;

/** Map an atom-map to its plain-value counterpart. */
type InferState<M extends AtomMap> = {
  [K in keyof M]: InferAtomType<M[K]>;
};

/** Unwrap getter return types. */
type InferGetters<G> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [K in keyof G]: G[K] extends (...args: any[]) => infer R ? R : unknown;
};

/**
 * The full store instance exposed to consumers.
 *
 * Union of state values, getter return values, action methods,
 * and the built-in `$reset()` helper.
 */
type StoreInstance<
  S extends AtomMap,
  G extends Record<string, Function>, // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  A extends Record<string, Function> // eslint-disable-line @typescript-eslint/no-unsafe-function-type
> = InferState<S> & InferGetters<G> & A & { $reset(): void };

/* ── Public API types ────────────────────────────────────────────── */

/**
 * The `DtfSelf` type is the `this` context inside getters and actions.
 * It gives full access to every state key, every getter, every action,
 * plus arbitrary undeclared keys (used by the proxy's runtime fallback).
 *
 * Because `noPropertyAccessFromIndexSignature` is enabled in this repo,
 * we cannot mix an index signature with named properties. Instead we use
 * `any` for `this` inside getters/actions — this is safe because the
 * proxy handles all property access at runtime.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DtfSelf = any;

type FacadeOptions<
  S extends AtomMap,
  G extends Record<string, Function>, // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  A extends Record<string, Function> // eslint-disable-line @typescript-eslint/no-unsafe-function-type
> = {
  jotaiStore: JotaiStore;
  state: S;
  getters?: G & ThisType<DtfSelf>;
  actions?: A & ThisType<DtfSelf>;
};

type UseStoreHook<T> = {
  (): T;
  store: T;
};

export function createJotaiPiniaFacadeForReact<
  S extends AtomMap,
  G extends Record<string, Function>, // eslint-disable-line @typescript-eslint/no-unsafe-function-type
  A extends Record<string, Function> // eslint-disable-line @typescript-eslint/no-unsafe-function-type
>({
  jotaiStore,
  state: rootAtoms,
  getters: getterDefs = {} as G,
  actions: actionDefs = {} as A
}: FacadeOptions<S, G, A>): UseStoreHook<StoreInstance<S, G, A>> {
  setAutoFreeze(false);

  type Instance = StoreInstance<S, G, A>;

  // Storage for keys written via set() that are not declared in rootAtoms.
  const undeclaredState: Record<string, unknown> = {};

  // ── combined atom for React subscriptions ────────────────────────────
  // Tracks all root atoms. Uses a version counter with reference equality
  // to detect atom changes. This is sufficient because Jotai already
  // deduplicates by reference internally, and the $prevAtomValues cache
  // ensures the version only increments when an atom actually changes.

  const allAtoms = Object.values(rootAtoms) as WritableAtom[];
  let $combinedVersion = 0;
  const $prevAtomValues = new Map<WritableAtom, unknown>();

  const $combined = atom((get: (a: unknown) => unknown) => {
    let anyChanged = false;
    for (const a of allAtoms) {
      const next = get(a);
      if (!$prevAtomValues.has(a) || $prevAtomValues.get(a) !== next) {
        anyChanged = true;
        $prevAtomValues.set(a, next);
      }
    }
    // eslint-disable-next-line no-plusplus
    if (anyChanged) $combinedVersion++;
    return $combinedVersion;
  });

  // ── store proxy ───────────────────────────────────────────────────────

  // eslint-disable-next-line prefer-const, @typescript-eslint/no-explicit-any
  let store: any = {};

  store = new Proxy({} as Instance, {
    get(_target, key: string) {
      if (key === "$reset") {
        return () => {
          for (const [, a] of Object.entries(rootAtoms)) {
            jotaiStore.set(a as WritableAtom, a.init);
          }
        };
      }

      if (key in actionDefs) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-function-type
        return (...args: any[]) => (actionDefs[key as keyof A] as Function).call(store, ...args);
      }

      if (key in getterDefs) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
        return (getterDefs[key as keyof G] as Function).call(store);
      }

      if (key in rootAtoms) {
        return jotaiStore.get(rootAtoms[key] as WritableAtom);
      }

      return undeclaredState[key];
    },

    set(_target, key: string, value: unknown) {
      if (key in rootAtoms) {
        // Jotai treats function values as updater callbacks (calling fn(prevValue)).
        // Wrap functions in a thunk so Jotai stores the function itself, matching
        // Pinia/Vue behaviour where state assignments are always stored as-is.
        const atomValue = typeof value === "function" ? () => value : value;
        jotaiStore.set(rootAtoms[key] as WritableAtom, atomValue as never);
      } else {
        undeclaredState[key] = value;
      }
      return true;
    }
  });

  // ── factory (React hook) ──────────────────────────────────────────────

  function useStore(): Instance {
    useAtomValue($combined, { store: jotaiStore as never });
    return store;
  }

  // Expose the raw proxy for non-React contexts (init functions, action cross-calls, etc.)
  useStore.store = store;

  return useStore as UseStoreHook<Instance>;
}
